import { groupBy } from '@bugbug/core/utils/toolbox';
import { useCallback, useMemo } from 'react';

import type { SelectedStep } from '../uiState.types';

import type { Group } from '@bugbug/core/types/groups';
import type { Step } from '@bugbug/core/types/steps';
import { useAppDispatch, useAppSelector } from '~/modules/store';
import {
  selectAllGroupsInstances,
  selectGroups,
  selectSortedSteps,
} from '~/modules/tests/tests.selectors';

import { UIStateActions } from '../uiState.redux';
import {
  selectCheckedSteps,
  selectCheckedStepsByGroups,
  selectLastFocusedStepCheckbox,
} from '../uiState.selectors';

export const useCheckedSteps = () => {
  const dispatch = useAppDispatch();
  const checkedStepsByGroups = useAppSelector(selectCheckedStepsByGroups);
  const checkedSteps = useAppSelector(selectCheckedSteps);
  const sortedSteps = useAppSelector(selectSortedSteps);
  const allGroups = useAppSelector(selectGroups(false));
  const allGroupsInstances = useAppSelector(selectAllGroupsInstances);
  const lastFocusedStepCheckbox = useAppSelector(selectLastFocusedStepCheckbox);

  const isCompletelyCheckedGroup = useCallback(
    ({ groupId }: { groupId: Group['id'] }) => {
      const allGroupSteps = allGroups[groupId].steps.map((id) => ({ id, groupId }));
      const allSelectedSteps = checkedStepsByGroups[groupId];
      if (!allSelectedSteps) return false;

      return allGroupSteps.every((step) => allSelectedSteps.includes(step.id));
    },
    [allGroups, checkedStepsByGroups],
  );

  const isIndeterminateCheckedGroup = useCallback(
    ({ groupId }: { groupId: Group['id'] }) =>
      allGroups[groupId]?.steps.some((stepId) =>
        checkedSteps.some((step) => step.id === stepId && step.groupId === groupId),
      ),
    [allGroups, checkedSteps],
  );

  const updateCheckedStepsInGroup = useCallback(
    ({
      steps,
      groupId,
      checked,
    }: {
      steps: SelectedStep[];
      groupId: Group['id'];
      checked: boolean;
    }) => {
      if (!steps.length) {
        dispatch(UIStateActions.setCheckedEmptyGroup({ groupId, checked }));
        return;
      }

      // Set steps state for source group
      dispatch(UIStateActions.setCheckedSteps({ steps, checked }));

      // Premature finish if there are no other instances of the source group
      if (allGroupsInstances[groupId].length === 1) return;

      /*
       * A test can contain multiple instances of the same component (group).
       * so that's why we need to populate checked steps for all instances of the source component.
       * With some exceptions:
       * - (1) if after last action all steps in the source component are checked, we should uncheck all instances that are partially checked
       * - (2) if before last action all steps in the source component were checked and are unchecked, we should uncheck only the group where the change was made.
       * - (3) if before last action all steps in the source component were checked and one step from the component was unchecke,
       *       we should copy state of the component to all instances with indeterminate state
       */
      const targetCheckedSteps = [
        ...checkedSteps.filter((step) => step.groupId === groupId),
        ...steps,
      ];

      const willGroupHaveIndeterminateState = checked
        ? !allGroups[groupId]?.steps.every((stepId) =>
            targetCheckedSteps.map(({ id }) => id).includes(stepId),
          ) // partial check
        : allGroups[groupId]?.steps.length !== steps.length; // partial uncheck

      const allPartiallyCheckedInstances = allGroupsInstances[groupId].filter(
        (instance) =>
          instance.id !== groupId && !isCompletelyCheckedGroup({ groupId: instance.id }),
      );

      if (checked && !willGroupHaveIndeterminateState) {
        // Scenario #1
        allPartiallyCheckedInstances.forEach((instance) => {
          dispatch(
            UIStateActions.setCheckedSteps({
              steps: instance.steps.map((id) => ({ id, groupId: instance.id })),
              checked: false,
            }),
          );
        });
      } else if (willGroupHaveIndeterminateState) {
        if (checked) {
          // Scenario #2
          dispatch(
            UIStateActions.setCheckedSteps({
              steps: allPartiallyCheckedInstances.flatMap((instance) =>
                steps.map((step) => ({ ...step, groupId: instance.id })),
              ),
              checked,
            }),
          );
          return;
        }

        // Scenario #3
        const uncheckedStepsIds = steps.map(({ id }) => id);
        const targetGroupCheckedSteps = checkedSteps.filter(
          (step) => step.groupId === groupId && !uncheckedStepsIds.includes(step.id),
        );
        const allStepsInStances = allPartiallyCheckedInstances.flatMap((instance) =>
          steps.map((step) => ({ id: step.id, groupId: instance.id })),
        );

        dispatch(
          UIStateActions.setCheckedSteps({
            steps: allStepsInStances,
            checked: false,
          }),
        );
        dispatch(
          UIStateActions.setCheckedSteps({
            steps: allPartiallyCheckedInstances.flatMap((instance) =>
              targetGroupCheckedSteps.map((step) => ({ id: step.id, groupId: instance.id })),
            ),
            checked: true,
          }),
        );
      }
    },
    [allGroups, allGroupsInstances, checkedSteps, dispatch, isCompletelyCheckedGroup],
  );

  const setCheckedStepInGroup = useCallback(
    ({ step, checked }: { step: SelectedStep; checked: boolean }) => {
      updateCheckedStepsInGroup({ steps: [step], groupId: step.groupId, checked });
      /*
       * Set last focused step for source group
       * (it will be used to continue checking/unchecking steps with shift key)
       */
      dispatch(UIStateActions.setLastFocusedStepCheckbox({ step }));
    },
    [dispatch, updateCheckedStepsInGroup],
  );

  const setCheckedGroup = useCallback(
    ({ groupId, checked }: { groupId: Group['id']; checked: boolean }) => {
      const allGroupSteps = allGroups[groupId].steps.map((id) => ({ id, groupId }));
      updateCheckedStepsInGroup({ steps: allGroupSteps, groupId, checked });

      dispatch(
        UIStateActions.setLastFocusedStepCheckbox({
          step: checked ? allGroupSteps.at(-1) : allGroupSteps[0],
        }),
      );
    },
    [allGroups, dispatch, updateCheckedStepsInGroup],
  );

  const continueCheckingFromLastStep = useCallback(
    ({ stepId, groupId: sourceGroupId }: { stepId: Step['id']; groupId: Group['id'] }) => {
      const isSourceStep = (step) => step.id === stepId && step.groupId === sourceGroupId;
      const isChecked = checkedSteps.some(isSourceStep);
      const stepIndex = sortedSteps.findIndex(isSourceStep);
      const lastFocusedStepIndex = sortedSteps.findIndex(
        (step) =>
          step.id === lastFocusedStepCheckbox?.id &&
          step.groupId === lastFocusedStepCheckbox?.groupId,
      );
      const rangeStartIndex = Math.min(stepIndex, lastFocusedStepIndex);
      const rangeEndIndex = Math.max(stepIndex, lastFocusedStepIndex);
      const stepsToCheck: Step[] = sortedSteps.slice(rangeStartIndex, rangeEndIndex + 1);

      if (stepsToCheck.length === 0) return;

      const stepsToCheckByGroup = groupBy('groupId', stepsToCheck);
      // eslint-disable-next-line no-restricted-syntax
      for (const [groupId, steps] of Object.entries(stepsToCheckByGroup)) {
        updateCheckedStepsInGroup({
          steps,
          groupId,
          checked: !isChecked,
        });
      }

      dispatch(
        UIStateActions.setLastFocusedStepCheckbox({
          step: !isChecked ? stepsToCheck[stepsToCheck.length - 1] : stepsToCheck[0],
        }),
      );
    },
    [
      checkedSteps,
      sortedSteps,
      dispatch,
      lastFocusedStepCheckbox?.id,
      lastFocusedStepCheckbox?.groupId,
      updateCheckedStepsInGroup,
    ],
  );

  const uncheckAllSteps = useCallback(() => {
    dispatch(UIStateActions.resetCheckedSteps());
  }, [dispatch]);

  return useMemo(
    () => ({
      setCheckedStepInGroup,
      setCheckedGroup,
      isCheckedGroup: isCompletelyCheckedGroup,
      isIndeterminateCheckedGroup,
      uncheckAllSteps,
      continueCheckingFromLastStep,
      checkedSteps,
    }),
    [
      setCheckedStepInGroup,
      setCheckedGroup,
      uncheckAllSteps,
      continueCheckingFromLastStep,
      isIndeterminateCheckedGroup,
      isCompletelyCheckedGroup,
      checkedSteps,
    ],
  );
};
